import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Select } from "@outpave/ds";
import type { SelectOption } from "@outpave/ds";
import { GET_CONTACT, GET_CONTACTS } from "../../graphql/contact";
import ContactType from "../../enums/ContactType";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { DEFAULT_PAGINATION_SIZE } from "./Constants";
import { useSelectSearch } from "hooks/useSelectSearch";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import ContactStatus from "enums/ContactStatus";
import ContactDrawerRouter from "components/admin/contacts/ContactDrawerRouter";
import { contactName } from "utils/fullName";
import { isChildContact } from "utils/contacts/payee";
import { useTranslation } from "react-i18next";

export type ContactData = {
  id: string;
  firstName: string;
  lastName?: string | null;
  email: string;
  phone: string;
  businessName: string;
  contactType: ContactType | null;
  address: any;
  status: ContactStatus;
  parentContactId: string | null;
  paymentBankInfo: string;
};

/** How a contact reads in the field and in the list. */
const contactLabel = (contact: ContactData) => {
  const name = contactName(contact);

  return name === contact.email ? contact.email : `${name} (${contact.email})`;
};

interface ContactSearchAutoCompleteProps {
  contactId?: string;
  contactType?: ContactType;
  status?: ContactStatus;
  selectedSecondaryRecipients?: ContactData[];
  showAddContactInDropdown?: boolean;
  customPlaceholder?: string;
  multiple?: boolean;
  hideLabel?: boolean;
  required?: boolean;
  errorText?: string | React.ReactNode;
  helperText?: string | React.ReactNode;
  suppressOnSelectOnExternalChange?: boolean;
  forcePickBusiness?: boolean;
  refetchIndex?: number;
  disabled?: boolean;
  customAddContactInDropdownFunction?: () => void;
  onSelectContact: (contact: ContactData | ContactData[] | null) => void;
  /** Formik's `handleBlur`, to mark the field touched. */
  handleBlur?: React.FocusEventHandler<HTMLInputElement>;
}

/**
 * The contact picker. Server-searched through `useSelectSearch`.
 *
 * "Add Contact" used to be a fake contact spliced into the results, which every
 * selection path then had to recognise and strip back out. It is a DS
 * `topAction` now: pinned above the list, outside it, and it never emits a
 * value — so the id can no longer leak into a selection.
 *
 * The hydration effects below are unchanged. They resolve a `contactId` prop
 * (and a freshly created contact) into the full record the caller expects back.
 */
const ContactSearchAutoComplete: React.FC<ContactSearchAutoCompleteProps> = ({
  contactId,
  contactType,
  status,
  showAddContactInDropdown = false,
  customPlaceholder,
  hideLabel = false,
  required = false,
  errorText = "",
  helperText = "",
  multiple = false,
  selectedSecondaryRecipients = [],
  suppressOnSelectOnExternalChange = false,
  forcePickBusiness = false,
  refetchIndex = 0,
  disabled = false,
  customAddContactInDropdownFunction,
  onSelectContact,
  handleBlur,
}) => {
  const { t } = useTranslation();
  const client = useApolloClient();

  const [hydratedValue, setHydratedValue] = useState<ContactData | null>(null);
  const [openContactDrawer, setOpenContactDrawer] = useState(false);
  const [newContactId, setNewContactId] = useState<string | null>(null);
  const [secondaryRecipients, setSecondaryRecipients] = useState<ContactData[]>(
    selectedSecondaryRecipients || [],
  );
  const [refetchKey, setRefetchKey] = useState(0);
  // Options carry only `{ id, label }`; callers want the record back.
  const seenRef = useRef(new Map<string, ContactData>());

  const placeholderText =
    customPlaceholder ||
    (contactType === ContactType.Business
      ? "Search Business Contacts"
      : contactType === ContactType.Individual
        ? "Search Individual Contacts"
        : "Search Contacts");

  const [fetchContactById, { data: contactByIdData }] =
    useLazyQuery(GET_CONTACT);

  const fetchPage = useCallback(
    async (query: string, after: string | null) => {
      const { data } = await client.query({
        query: GET_CONTACTS,
        variables: {
          searchTerm: query,
          contactType,
          status,
          first: DEFAULT_PAGINATION_SIZE,
          after: after ?? undefined,
        },
        fetchPolicy: "network-only",
      });
      const connection = data?.contacts;
      const contacts: ContactData[] = (connection?.edges ?? []).map(
        ({ node }: { node: ContactData }) => ({ ...node }),
      );
      contacts.forEach((c) => seenRef.current.set(c.id, c));
      return {
        options: contacts.map((c) => ({ id: c.id, label: contactLabel(c) })),
        hasNextPage: connection?.pageInfo?.hasNextPage ?? false,
        endCursor: connection?.pageInfo?.endCursor ?? null,
      };
    },
    [client, contactType, status],
  );

  const seed = useMemo<SelectOption | null>(
    () =>
      hydratedValue
        ? { id: hydratedValue.id, label: contactLabel(hydratedValue) }
        : null,
    [hydratedValue],
  );

  const search = useSelectSearch<SelectOption>({ fetchPage, seed });

  // A chosen recipient must stay listed even when the current search no longer
  // matches it, or the DS cannot resolve its chip's label.
  const options = useMemo(() => {
    const listed = new Set(search.options.map((o) => o.id));
    const missing = secondaryRecipients
      .filter((c) => !listed.has(c.id))
      .map((c) => ({ id: c.id, label: contactLabel(c) }));
    return multiple ? [...missing, ...search.options] : search.options;
  }, [search.options, secondaryRecipients, multiple]);

  secondaryRecipients.forEach((c) => seenRef.current.set(c.id, c));

  const openAddContact = () => {
    if (customAddContactInDropdownFunction) {
      customAddContactInDropdownFunction();
      return;
    }
    setOpenContactDrawer(true);
  };

  const handleCloseAddContactDrawer = (
    refresh?: boolean,
    openContactDetailsWithId?: string,
  ) => {
    setOpenContactDrawer(false);
    if (openContactDetailsWithId) setNewContactId(openContactDetailsWithId);
  };

  const selectSingle = (id: string | null) => {
    const contact = id ? (seenRef.current.get(id) ?? null) : null;

    // Picking an individual under a business resolves to the parent instead.
    if (forcePickBusiness && contact && isChildContact(contact)) {
      setNewContactId(contact.parentContactId);
      return;
    }

    if (!id) search.reset();
    setHydratedValue(contact);
    onSelectContact(contact);
    setNewContactId(null);
  };

  const selectMany = (ids: string[]) => {
    const contacts = ids
      .map((id) => seenRef.current.get(id))
      .filter((c): c is ContactData => !!c);
    setSecondaryRecipients(contacts);
    onSelectContact(contacts.length ? contacts : null);
  };

  useEffect(() => {
    if (contactByIdData?.contact) {
      const contact: ContactData = { ...contactByIdData.contact };
      seenRef.current.set(contact.id, contact);

      if (multiple) {
        setSecondaryRecipients((prev) => {
          if (prev.some((c) => c.id === contact.id)) return prev;
          const updated = [...prev, contact];
          if (!suppressOnSelectOnExternalChange) {
            onSelectContact(updated);
          }
          return updated;
        });
      } else if (hydratedValue?.id !== contact.id) {
        setHydratedValue(contact);
        if (!suppressOnSelectOnExternalChange) {
          onSelectContact(contact);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactByIdData, suppressOnSelectOnExternalChange]);

  useEffect(() => {
    if (multiple && Array.isArray(selectedSecondaryRecipients)) {
      setSecondaryRecipients(selectedSecondaryRecipients);
    }
  }, [selectedSecondaryRecipients, multiple]);

  useEffect(() => {
    if (contactId && (hydratedValue?.id !== contactId || refetchIndex > 0)) {
      setRefetchKey((k) => k + 1);
      fetchContactById({
        variables: { id: contactId, refetchKey: refetchKey + 1 },
        fetchPolicy: "cache-and-network",
      });
      return;
    }

    if (newContactId && hydratedValue?.id !== newContactId) {
      setRefetchKey((k) => k + 1);
      fetchContactById({
        variables: { id: newContactId, refetchKey: refetchKey + 1 },
        fetchPolicy: "cache-and-network",
      });
      return;
    }

    if (!contactId) {
      setHydratedValue(null);
      setNewContactId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, newContactId, refetchIndex]);

  const shared = {
    label: hideLabel ? undefined : t(placeholderText),
    "aria-label": hideLabel ? t(placeholderText) : undefined,
    placeholder: hideLabel ? t(placeholderText) : undefined,
    required,
    disabled,
    onBlur: handleBlur,
    errorText: errorText || undefined,
    helperText: helperText || undefined,
    options,
    loading: search.loading,
    highlight: search.query,
    onInputChange: search.onInputChange,
    onScrollEnd: search.onScrollEnd,
    ...(showAddContactInDropdown
      ? { topAction: { label: t("Add Contact"), onClick: openAddContact } }
      : {}),
  };

  return (
    <>
      {multiple ? (
        <Select
          {...shared}
          multiple
          multipleLabel={t("Recipients")}
          value={secondaryRecipients.map((c) => c.id)}
          onChange={selectMany}
        />
      ) : (
        <Select
          {...shared}
          value={hydratedValue?.id ?? null}
          onChange={selectSingle}
        />
      )}

      {openContactDrawer && (
        <ContactDrawerRouter
          open={openContactDrawer}
          drawerType={ContactRouterDrawerType.NewContact}
          onClose={handleCloseAddContactDrawer}
        />
      )}
    </>
  );
};

export default ContactSearchAutoComplete;
